'use client';

import React, { useState, useEffect, useRef } from 'react';
import { colors, baseStyles, formatUSD, formatNum } from './SharedUI';

const formatSmartPrice = (val) => {
  if (!val || isNaN(val)) return '--';
  const num = parseFloat(val);
  if (num >= 1000) return `$${formatNum(num, 2)}`;
  if (num >= 1) return `$${formatNum(num, 4)}`;
  if (num >= 0.001) return `$${formatNum(num, 6)}`;
  return `$${formatNum(num, 8)}`;
};

export default function SmartSignalRadar({ onOpenRadar }) {
  // ==========================================
  // STATE BƯỚC 1: Lọc Volume 24h
  // ==========================================
  const [minVolM, setMinVolM] = useState(10);   
  const [maxVolM, setMaxVolM] = useState(1000); 
  const [step1Loading, setStep1Loading] = useState(false);
  
  // ==========================================
  // STATE BƯỚC 2: Watchlist
  // ==========================================
  const [rawCoinList, setRawCoinList] = useState([]); 
  const [coinList, setCoinList] = useState([]);       
  const [selectedCoins, setSelectedCoins] = useState(new Set());
  
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minPct, setMinPct] = useState('');
  const [maxPct, setMaxPct] = useState('');

  // ==========================================
  // STATE BƯỚC 3: Cấu hình Smart Signal
  // ==========================================
  const [minTotalVol, setMinTotalVol] = useState(1); // Đơn vị Triệu $
  
  // [CẬP NHẬT]: Sử dụng khoảng Min - Max cho độ lệch %
  const [minDiffPct, setMinDiffPct] = useState(0.5); 
  const [maxDiffPct, setMaxDiffPct] = useState(3.0); 
  
  // Filter Ratio: 'NONE', 'LONG', 'SHORT'
  const [globalRatioFilter, setGlobalRatioFilter] = useState('NONE');
  const [whaleRatioFilter, setWhaleRatioFilter] = useState('NONE');
  const [retailRatioFilter, setRetailRatioFilter] = useState('NONE');

  const [pendingAuto, setPendingAuto] = useState(false); 
  const [autoTime, setAutoTime] = useState(15); 
  const [isAuto, setIsAuto] = useState(false);           
  const [activeInterval, setActiveInterval] = useState(15); 

  const [step3Loading, setStep3Loading] = useState(false);
  const isScanningRef = useRef(false); 
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  
  const [scanResults, setScanResults] = useState([]);

  // ==========================================
  // [BỔ SUNG MỚI]: STATE & HÀM ĐỌC MA7/MA25
  // ==========================================
  const [maReports, setMaReports] = useState({});
  const [loadingMA, setLoadingMA] = useState({});

  const checkMA = async (symbol, livePrice) => {
    setLoadingMA(prev => ({ ...prev, [symbol]: true }));
    try {
      // Mặc định lấy khung 1h (1 giờ) để check MA
      const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=1h&limit=25`);
      const klines = await res.json();
      
      if (klines && klines.length >= 25) {
        // Lấy cột giá đóng cửa (Close Price)
        const closes = klines.map(k => parseFloat(k[4])); 
        
        // Tính MA7
        const closes7 = closes.slice(closes.length - 7);
        const ma7 = closes7.reduce((a, b) => a + b, 0) / 7;
        
        // Tính MA25
        const ma25 = closes.reduce((a, b) => a + b, 0) / 25;

        // So sánh MA7 và MA25
        const isMa7Above = ma7 > ma25;
        const diffMa = (Math.abs(ma7 - ma25) / ma25) * 100;

        // So sánh Live và MA7
        const isLiveAbove = livePrice > ma7;
        const diffLive = (Math.abs(livePrice - ma7) / ma7) * 100;

        setMaReports(prev => ({
          ...prev,
          [symbol]: { ma7, ma25, isMa7Above, diffMa, isLiveAbove, diffLive }
        }));
      } else {
        alert("Dữ liệu nến không đủ để tính MA25!");
      }
    } catch (error) {
      console.error("Lỗi khi đọc MA:", error);
    } finally {
      setLoadingMA(prev => ({ ...prev, [symbol]: false }));
    }
  };

  // ==========================================
  // HÀM UI HELPERS
  // ==========================================
  const handleWhaleChange = (val) => {
    setWhaleRatioFilter(val);
    if (val !== 'NONE') setRetailRatioFilter('NONE');
  };

  const handleRetailChange = (val) => {
    setRetailRatioFilter(val);
    if (val !== 'NONE') setWhaleRatioFilter('NONE');
  };

  const getRatioStatus = (ratioVal) => {
    if (ratioVal === undefined || ratioVal === null || isNaN(ratioVal)) return <span style={{ color: colors.textSub }}>N/A</span>;
    if (ratioVal > 1) return <span style={{ color: colors.green, fontWeight: 'bold' }}>LONG ({ratioVal.toFixed(2)})</span>;
    return <span style={{ color: colors.red, fontWeight: 'bold' }}>SHORT ({ratioVal.toFixed(2)})</span>;
  };

  // ==========================================
  // HÀM: QUÉT TỔNG THỂ (BƯỚC 1)
  // ==========================================
  const fetchMarketOverview = async () => {
    setStep1Loading(true);
    setRawCoinList([]); setCoinList([]); setScanResults([]); setMaReports({});
    try {
      const exRes = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const exInfo = await exRes.json();
      const validSymbols = new Set(
        exInfo.symbols
          .filter(s => s.quoteAsset === 'USDT' && s.status === 'TRADING' && s.contractType === 'PERPETUAL')
          .map(s => s.symbol)
      );
      const tkRes = await fetch('https://fapi.binance.com/fapi/v1/ticker/24hr');
      const tkData = await tkRes.json();
      const actualMinVol = parseFloat(minVolM) * 1000000;
      const actualMaxVol = parseFloat(maxVolM) * 1000000;
      
      const filtered = tkData
        .filter(t => validSymbols.has(t.symbol))
        .map(t => ({
          symbol: t.symbol,
          lastPrice: parseFloat(t.lastPrice),
          priceChange: parseFloat(t.priceChangePercent),
          quoteVolume: parseFloat(t.quoteVolume)
        }))
        .filter(t => t.quoteVolume >= actualMinVol && t.quoteVolume <= actualMaxVol)
        .sort((a, b) => b.quoteVolume - a.quoteVolume);
      
      setRawCoinList(filtered); 
      setCoinList(filtered);
      setSelectedCoins(new Set(filtered.map(c => c.symbol))); 
    } catch (error) {
      console.error("Lỗi Quét Thị Trường:", error);
      alert("Lỗi kết nối API Binance!");
    } finally {
      setStep1Loading(false);
    }
  };

  const handleStep2Filter = () => {
    let temp = [...rawCoinList]; 
    if (minPrice !== '') temp = temp.filter(c => c.lastPrice >= parseFloat(minPrice));
    if (maxPrice !== '') temp = temp.filter(c => c.lastPrice <= parseFloat(maxPrice));
    if (minPct !== '') temp = temp.filter(c => c.priceChange >= parseFloat(minPct));
    if (maxPct !== '') temp = temp.filter(c => c.priceChange <= parseFloat(maxPct));
    setCoinList(temp);
    setSelectedCoins(new Set(temp.map(c => c.symbol))); 
  };

  const toggleSelectAll = () => {
    if (selectedCoins.size === coinList.length) setSelectedCoins(new Set()); 
    else setSelectedCoins(new Set(coinList.map(c => c.symbol))); 
  };

  const toggleCoin = (symbol) => {
    const newSet = new Set(selectedCoins);
    if (newSet.has(symbol)) newSet.delete(symbol);
    else newSet.add(symbol);
    setSelectedCoins(newSet);
  };

  const handleSaveMode = () => {
    setIsAuto(pendingAuto);
    setActiveInterval(autoTime); 
    if (pendingAuto && !isScanningRef.current) {
      executeSignalRadar();
    }
  };

  // ==========================================
  // HÀM: QUÉT SMART SIGNAL & DÒNG TIỀN (BƯỚC 3)
  // ==========================================
  const executeSignalRadar = async () => {
    if (selectedCoins.size === 0) {
      if (!isAuto) alert("Vui lòng chọn ít nhất 1 đồng Coin ở Bước 2!");
      return;
    }
    if (isScanningRef.current) return;
    isScanningRef.current = true;
    setStep3Loading(true);
    setScanResults([]); 
    setMaReports({}); // Reset báo cáo MA khi quét mới
    
    const targets = Array.from(selectedCoins);
    setProgress({ current: 0, total: targets.length });
    const bufferResults = [];

    const actualMinTotalVol = parseFloat(minTotalVol) * 1000000;

    for (let i = 0; i < targets.length; i++) {
      const symbol = targets[i];
      setProgress({ current: i + 1, total: targets.length });
      
      try {
        const coinInfo = rawCoinList.find(c => c.symbol === symbol);
        if (!coinInfo) continue;
        
        const livePrice = coinInfo.lastPrice;
        const quoteVol = coinInfo.quoteVolume;

        if (quoteVol < actualMinTotalVol) continue;

        const sigRes = await fetch(`https://www.binance.com/bapi/futures/v1/public/future/smart-money/signal/overview?symbol=${symbol}`);
        const sigJson = await sigRes.json();
        
        if (!sigJson || !sigJson.success || !sigJson.data) continue;
        const data = sigJson.data;

        // --- BÓC TÁCH TỶ LỆ (RATIOS) ---
        const globalAccRatio = data.shortTraders > 0 ? data.longTraders / data.shortTraders : 0;
        const globalPosRatio = data.shortTradersQty > 0 ? data.longTradersQty / data.shortTradersQty : 0;
        const whaleAccRatio = data.shortWhales > 0 ? data.longWhales / data.shortWhales : 0;
        const whalePosRatio = data.shortWhalesQty > 0 ? data.longWhalesQty / data.shortWhalesQty : 0;

        const retailLongTraders = Math.max(0, data.longTraders - data.longWhales);
        const retailShortTraders = Math.max(0, data.shortTraders - data.shortWhales);
        const retailAccRatio = retailShortTraders > 0 ? retailLongTraders / retailShortTraders : 0;
        const retailLongQty = Math.max(0, data.longTradersQty - data.longWhalesQty);
        const retailShortQty = Math.max(0, data.shortTradersQty - data.shortWhalesQty);
        const retailPosRatio = retailShortQty > 0 ? retailLongQty / retailShortQty : 0;

        // --- XÁC ĐỊNH PHE ÁP ĐẢO (Trader HOẶC Volume) ---
        const isGlobalLong = globalAccRatio > 1 || globalPosRatio > 1;
        const isGlobalShort = globalAccRatio < 1 || globalPosRatio < 1;

        const isWhaleLong = whaleAccRatio > 1 || whalePosRatio > 1;
        const isWhaleShort = whaleAccRatio < 1 || whalePosRatio < 1;

        const isRetailLong = retailAccRatio > 1 || retailPosRatio > 1;
        const isRetailShort = retailAccRatio < 1 || retailPosRatio < 1;

        // --- LỌC ĐIỀU KIỆN PHE PHÁI TỪ KHU VỰC 3.2 ---
        if (globalRatioFilter === 'LONG' && !isGlobalLong) continue;
        if (globalRatioFilter === 'SHORT' && !isGlobalShort) continue;
        
        if (whaleRatioFilter === 'LONG' && !isWhaleLong) continue;
        if (whaleRatioFilter === 'SHORT' && !isWhaleShort) continue;
        
        if (retailRatioFilter === 'LONG' && !isRetailLong) continue;
        if (retailRatioFilter === 'SHORT' && !isRetailShort) continue;

        // --- XÁC ĐỊNH GIÁ ENTRY DỰA THEO BỘ LỌC CHỌN ---
        let entry = null;
        let entryType = '';

        if (whaleRatioFilter === 'LONG') {
            entry = parseFloat(data.longWhalesAvgEntryPrice);
            entryType = "Whale LONG";
        } else if (whaleRatioFilter === 'SHORT') {
            entry = parseFloat(data.shortWhalesAvgEntryPrice);
            entryType = "Whale SHORT";
        } else if (retailRatioFilter === 'LONG') {
            entry = parseFloat(data.longTradersAvgEntryPrice);
            entryType = "Global LONG (Đại diện Retail)";
        } else if (retailRatioFilter === 'SHORT') {
            entry = parseFloat(data.shortTradersAvgEntryPrice);
            entryType = "Global SHORT (Đại diện Retail)";
        } else if (globalRatioFilter === 'LONG') {
            entry = parseFloat(data.longTradersAvgEntryPrice);
            entryType = "Global LONG";
        } else if (globalRatioFilter === 'SHORT') {
            entry = parseFloat(data.shortTradersAvgEntryPrice);
            entryType = "Global SHORT";
        } else {
            // Mặc định nếu không lọc: Chọn phe Cá mập đang có Volume (Position) lớn hơn
            if (whalePosRatio > 1) {
                entry = parseFloat(data.longWhalesAvgEntryPrice);
                entryType = "Whale LONG (Tự Động)";
            } else {
                entry = parseFloat(data.shortWhalesAvgEntryPrice);
                entryType = "Whale SHORT (Tự Động)";
            }
        }
        
        if (!entry || isNaN(entry)) continue;

        // --- LỌC THEO KHOẢNG CÁCH MIN - MAX (%) ---
        const actualDiffPct = ((livePrice - entry) / entry) * 100;
        const absDiff = Math.abs(actualDiffPct);
        const reqMin = parseFloat(minDiffPct);
        const reqMax = parseFloat(maxDiffPct);
        
        if (absDiff < reqMin || absDiff > reqMax) continue;

        // --- BÓC TÁCH TỶ LỆ ĐANG LÃI (WIN RATE %) ---
        const longProfitTraders = data.longProfitTraders || 0;
        const shortProfitTraders = data.shortProfitTraders || 0;
        const longProfitWhales = data.longProfitWhales || 0;
        const shortProfitWhales = data.shortProfitWhales || 0;
        
        const retailLongProfit = Math.max(0, longProfitTraders - longProfitWhales);
        const retailShortProfit = Math.max(0, shortProfitTraders - shortProfitWhales);

        const globalLongWinRate = data.longTraders > 0 ? (longProfitTraders / data.longTraders) * 100 : 0;
        const globalShortWinRate = data.shortTraders > 0 ? (shortProfitTraders / data.shortTraders) * 100 : 0;
        const whaleLongWinRate = data.longWhales > 0 ? (longProfitWhales / data.longWhales) * 100 : 0;
        const whaleShortWinRate = data.shortWhales > 0 ? (shortProfitWhales / data.shortWhales) * 100 : 0;
        const retailLongWinRate = retailLongTraders > 0 ? (retailLongProfit / retailLongTraders) * 100 : 0;
        const retailShortWinRate = retailShortTraders > 0 ? (retailShortProfit / retailShortTraders) * 100 : 0;

        bufferResults.push({
          symbol, livePrice, entry, entryType, actualDiffPct, quoteVol,
          globalAccRatio, globalPosRatio, 
          whaleAccRatio, whalePosRatio, 
          retailAccRatio, retailPosRatio,
          globalLongWinRate, globalShortWinRate,
          whaleLongWinRate, whaleShortWinRate,
          retailLongWinRate, retailShortWinRate,
          change24h: coinInfo.priceChange || 0,
        });
        setScanResults([...bufferResults]); 
        
        await new Promise(r => setTimeout(r, 100));
      } catch (e) { 
        // Bỏ qua lỗi nhẹ
      }
    }
    setStep3Loading(false);
    isScanningRef.current = false;
  };

  const scanFnRef = useRef(executeSignalRadar);
  useEffect(() => { scanFnRef.current = executeSignalRadar; }, [executeSignalRadar]);

  useEffect(() => {
    let intervalId;
    if (isAuto) {
      intervalId = setInterval(() => { scanFnRef.current(); }, activeInterval * 60 * 1000); 
    }
    return () => { if (intervalId) clearInterval(intervalId); }
  }, [isAuto, activeInterval]);

  return (
    <div style={{ ...baseStyles.card, marginBottom: '24px' }}>
      
      {/* HEADER */}
      <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <strong style={{ color: colors.textMain, fontSize: '16px' }}>📡 SMART SIGNAL & DÒNG TIỀN (ORDER FLOW)</strong>
        {isAuto && (
          <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', border: `1px solid ${colors.green}`, color: colors.green, padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '6px', height: '6px', backgroundColor: colors.green, borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></div>
            HỆ THỐNG ĐANG AUTO QUÉT ({activeInterval}p/lần)
          </div>
        )}
      </div>

      {/* BƯỚC 1 */}
      <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.border}` }}>
        <div style={{ ...colors.textSub, fontSize: '13px', marginBottom: '12px', fontWeight: 'bold' }}>🛑 BƯỚC 1: LỌC NHIỄU BẰNG VOLUME (API 24hr)</div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Min Vol 24h (Triệu $)</div><input type="number" value={minVolM} onChange={e => setMinVolM(e.target.value)} style={{ ...baseStyles.input, maxWidth: '140px' }} disabled={isAuto} /></div>
          <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Max Vol 24h (Triệu $)</div><input type="number" value={maxVolM} onChange={e => setMaxVolM(e.target.value)} style={{ ...baseStyles.input, maxWidth: '140px' }} disabled={isAuto} /></div>
          <button onClick={fetchMarketOverview} disabled={step1Loading || isAuto} style={{ ...baseStyles.select, backgroundColor: step1Loading || isAuto ? colors.border : '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>{step1Loading ? '⏳ ĐANG QUÉT...' : '⚡ QUÉT TỔNG THỂ 300+ COIN'}</button>
        </div>
      </div>

      {/* BƯỚC 2 */}
      {rawCoinList.length > 0 && (
        <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold' }}>🎯 BƯỚC 2: WATCHLIST <span style={{ color: colors.green, fontWeight: 'normal', marginLeft: '8px' }}>(Còn lại: {coinList.length} Coin)</span></div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: `1px solid ${colors.border}` }}>
            <input type="number" step="0.0001" placeholder="Giá Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} />
            <input type="number" step="0.0001" placeholder="Giá Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} />
            <input type="number" step="0.1" placeholder="% Thay Đổi Min" value={minPct} onChange={e => setMinPct(e.target.value)} style={{ ...baseStyles.input, maxWidth: '120px', fontSize: '12px' }} disabled={isAuto} />
            <input type="number" step="0.1" placeholder="% Thay Đổi Max" value={maxPct} onChange={e => setMaxPct(e.target.value)} style={{ ...baseStyles.input, maxWidth: '120px', fontSize: '12px' }} disabled={isAuto} />
            <div style={{ display: 'flex', alignItems: 'flex-end' }}><button onClick={handleStep2Filter} disabled={isAuto} style={{ ...baseStyles.select, backgroundColor: isAuto ? colors.border : '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>🎯 LỌC</button></div>
          </div>
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.textMain, backgroundColor: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '6px' }}>
              <input type="checkbox" checked={selectedCoins.size === coinList.length && coinList.length > 0} onChange={toggleSelectAll} style={{ width: '16px', height: '16px' }} disabled={isAuto} />
              <strong>CHỌN TẤT CẢ</strong>
            </label>
            <span style={{ fontSize: '12px', color: colors.textSub }}>Đang chọn: {selectedCoins.size} / {coinList.length} Coin.</span>
          </div>
          <div style={{ maxHeight: '350px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: colors.cardBg, borderBottom: `2px solid ${colors.border}`, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'center', width: '60px' }}>Chọn</th>
                  <th style={{ padding: '10px' }}>Tên Coin</th>
                  <th style={{ padding: '10px' }}>Giá Live</th>
                  <th style={{ padding: '10px' }}>24h Thay đổi</th>
                  <th style={{ padding: '10px' }}>24h Volume</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Soi Cản</th>
                </tr>
              </thead>
              <tbody>
                {coinList.map(coin => {
                  const isChecked = selectedCoins.has(coin.symbol);
                  return (
                    <tr key={coin.symbol} style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: isChecked ? 'transparent' : 'rgba(255,255,255,0.02)', opacity: isChecked ? 1 : 0.4 }}>
                      <td style={{ padding: '10px', textAlign: 'center' }}><input type="checkbox" checked={isChecked} onChange={() => toggleCoin(coin.symbol)} disabled={isAuto} /></td>
                      <td style={{ padding: '10px', fontWeight: 'bold', color: colors.textMain }}>{coin.symbol}</td>
                      <td style={{ padding: '10px', color: colors.textMain }}>{formatSmartPrice(coin.lastPrice)}</td>
                      <td style={{ padding: '10px', color: coin.priceChange >= 0 ? colors.green : colors.red }}>{coin.priceChange >= 0 ? '+' : ''}{coin.priceChange.toFixed(2)}%</td>
                      <td style={{ padding: '10px', color: colors.textSub }}>{formatUSD(coin.quoteVolume)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button 
                          onClick={() => onOpenRadar && onOpenRadar(coin.symbol)}
                          style={{ background: colors.greenBg, color: colors.green, border: `1px solid ${colors.green}`, borderRadius: '4px', padding: '4px 8px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          DÒ S/R
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BƯỚC 3 */}
      {coinList.length > 0 && (
        <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold' }}>⚙️ BƯỚC 3: CẤU HÌNH SMART SIGNAL & DÒNG TIỀN</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select value={autoTime} onChange={(e) => setAutoTime(Number(e.target.value))} style={{ ...baseStyles.input, padding: '4px 8px', width: '80px', fontSize: '12px' }} disabled={isAuto}>
                <option value={5}>5p</option><option value={10}>10p</option><option value={15}>15p</option><option value={30}>30p</option>
              </select>
              <div style={{ display: 'flex', backgroundColor: colors.bg, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                <button onClick={() => setPendingAuto(false)} style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: !pendingAuto ? 'rgba(255,255,255,0.1)' : 'transparent', color: !pendingAuto ? '#fff' : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}>🔘 MANUAL</button>
                <button onClick={() => setPendingAuto(true)} style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: pendingAuto ? colors.greenBg : 'transparent', color: pendingAuto ? colors.green : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}>🟢 AUTO</button>
              </div>
              <button onClick={handleSaveMode} style={{ ...baseStyles.select, backgroundColor: '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>💾 LƯU CHẾ ĐỘ</button>
            </div>
          </div>
          
          <div style={{ border: `1px dashed ${colors.warn}`, borderRadius: '8px', padding: '16px', marginBottom: '16px', backgroundColor: 'rgba(234, 179, 8, 0.05)' }}>
            <div style={{ fontSize: '12px', color: colors.warn, fontWeight: 'bold', marginBottom: '12px' }}>Khu Vực 3.1: Bộ Lọc Dòng Tiền & Độ Lệch Giá</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: colors.textSub }}>Tổng tiền đổ vào (Triệu $) &gt;</span>
                <input type="number" value={minTotalVol} onChange={e=>setMinTotalVol(e.target.value)} style={{...baseStyles.input, width: '120px'}} disabled={isAuto} />
              </div>
              {/* [CẬP NHẬT UI]: Khoảng cách Lệch Giá Min/Max */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: colors.textSub }}>Độ lệch Giá Live so với Entry (Tuyệt đối):</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: colors.textSub }}>Từ</span>
                  <input type="number" step="0.1" value={minDiffPct} onChange={e=>setMinDiffPct(e.target.value)} style={{...baseStyles.input, width: '60px'}} disabled={isAuto} />
                  <span style={{ fontSize: '12px', color: colors.textSub }}>% đến</span>
                  <input type="number" step="0.1" value={maxDiffPct} onChange={e=>setMaxDiffPct(e.target.value)} style={{...baseStyles.input, width: '60px'}} disabled={isAuto} />
                  <span style={{ fontSize: '12px', color: colors.textMain }}>%</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '8px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>Khu Vực 3.2: Định Vị Phe Phái (Được tính dựa trên Số lượng Tài khoản HOẶC Volume)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            {/* GLOBAL */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '6px', border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textMain, marginBottom: '10px' }}>🌍 Thị trường chung (Global)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: globalRatioFilter==='LONG' ? colors.green : colors.textSub }}><input type="radio" checked={globalRatioFilter==='LONG'} onChange={()=>setGlobalRatioFilter('LONG')} disabled={isAuto}/> LONG &gt; SHORT</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: globalRatioFilter==='SHORT' ? colors.red : colors.textSub }}><input type="radio" checked={globalRatioFilter==='SHORT'} onChange={()=>setGlobalRatioFilter('SHORT')} disabled={isAuto}/> SHORT &gt; LONG</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px' }}><input type="radio" checked={globalRatioFilter==='NONE'} onChange={()=>setGlobalRatioFilter('NONE')} disabled={isAuto}/> Không lọc</label>
              </div>
            </div>
            {/* WHALE */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '6px', border: `1px solid ${colors.border}`, opacity: retailRatioFilter !== 'NONE' ? 0.4 : 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textMain, marginBottom: '10px' }}>🐋 Cá Mập (Whale/Top Traders)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: whaleRatioFilter==='LONG' ? colors.green : colors.textSub }}><input type="radio" checked={whaleRatioFilter==='LONG'} onChange={()=>handleWhaleChange('LONG')} disabled={isAuto}/> LONG &gt; SHORT</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: whaleRatioFilter==='SHORT' ? colors.red : colors.textSub }}><input type="radio" checked={whaleRatioFilter==='SHORT'} onChange={()=>handleWhaleChange('SHORT')} disabled={isAuto}/> SHORT &gt; LONG</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px' }}><input type="radio" checked={whaleRatioFilter==='NONE'} onChange={()=>handleWhaleChange('NONE')} disabled={isAuto}/> Không lọc</label>
              </div>
            </div>
            {/* RETAIL */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '6px', border: `1px solid ${colors.border}`, opacity: whaleRatioFilter !== 'NONE' ? 0.4 : 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textMain, marginBottom: '10px' }}>🐟 Cá Con (Retail = Global - Whale)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: retailRatioFilter==='LONG' ? colors.green : colors.textSub }}><input type="radio" checked={retailRatioFilter==='LONG'} onChange={()=>handleRetailChange('LONG')} disabled={isAuto}/> LONG &gt; SHORT</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: retailRatioFilter==='SHORT' ? colors.red : colors.textSub }}><input type="radio" checked={retailRatioFilter==='SHORT'} onChange={()=>handleRetailChange('SHORT')} disabled={isAuto}/> SHORT &gt; LONG</label>
                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px' }}><input type="radio" checked={retailRatioFilter==='NONE'} onChange={()=>handleRetailChange('NONE')} disabled={isAuto}/> Không lọc</label>
              </div>
            </div>
          </div>

          <button onClick={executeSignalRadar} disabled={step3Loading || selectedCoins.size === 0 || isAuto} style={{ width: '100%', padding: '12px', fontSize: '14px', backgroundColor: (step3Loading || selectedCoins.size === 0 || isAuto) ? colors.border : '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
            {isAuto ? `🟢 ĐANG TỰ ĐỘNG QUÉT (${activeInterval}p)` : (step3Loading ? `⏳ ĐANG QUÉT API... (${progress.current}/${progress.total})` : `🚀 BẮT ĐẦU DÒ SMART SIGNAL`)}
          </button>
        </div>
      )}

      {/* KẾT QUẢ ĐẦU RA */}
      {(scanResults.length > 0 || (progress.current === progress.total && progress.total > 0 && !step3Loading)) && (
        <div style={{ marginTop: '24px', borderTop: `2px dashed ${colors.border}`, paddingTop: '20px' }}>
          <div style={{ color: colors.warn, fontWeight: 'bold', marginBottom: '16px', fontSize: '16px' }}>🚨 KẾT QUẢ: MỤC TIÊU VÀO TẦM NGẮM ({scanResults.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {scanResults.map((res, idx) => (
              <div key={idx} style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: `4px solid ${colors.green}`, borderRadius: '0 8px 8px 0', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '16px', color: colors.textMain }}>{idx + 1}. [ {res.symbol} ]</strong>
                      <span style={{ fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>Gap Live/Entry: <span style={{ color: res.actualDiffPct > 0 ? colors.green : colors.red }}>{res.actualDiffPct ? `${res.actualDiffPct>0?'+':''}${res.actualDiffPct.toFixed(2)}%` : 'N/A'}</span></span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', color: colors.green }}>🟢 THỎA ĐIỀU KIỆN</span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', fontSize: '12px', color: colors.textSub }}>
                      {/* CỘT 1: THÔNG TIN CHUNG */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>• Giá Live: <strong style={{color: colors.textMain}}>{formatSmartPrice(res.livePrice)}</strong> <span style={{ color: res.change24h >= 0 ? colors.green : colors.red }}>({res.change24h >= 0 ? '+' : ''}{res.change24h.toFixed(2)}%)</span></div>
                        <div>• Entry (<span style={{color: res.entryType.includes('LONG') ? colors.green : colors.red}}>{res.entryType}</span>): <strong style={{color: colors.warn}}>{formatSmartPrice(res.entry)}</strong></div>
                        <div>• Volume 24h: <strong style={{ color: colors.textMain }}>{formatUSD(res.quoteVol)}</strong></div>
                      </div>
                      
                      {/* CỘT 2: TỶ LỆ PHE PHÁI */}
                      <div style={{ borderLeft: `1px solid ${colors.border}`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: colors.textMain, fontWeight: 'bold', marginBottom: '4px' }}>Tỷ Lệ Phe Phái (Long/Short Ratios):</span>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <span>🌍 Global (TK): {getRatioStatus(res.globalAccRatio)}</span>
                          <span>🌍 Global (Vol): {getRatioStatus(res.globalPosRatio)}</span>
                          <span>🐋 Whale (TK): {getRatioStatus(res.whaleAccRatio)}</span>
                          <span>🐋 Whale (Vol): {getRatioStatus(res.whalePosRatio)}</span>
                          <span>🐟 Retail (TK): {getRatioStatus(res.retailAccRatio)}</span>
                          <span>🐟 Retail (Vol): {getRatioStatus(res.retailPosRatio)}</span>
                        </div>
                      </div>

                      {/* CỘT 3: WIN RATE (% ĐANG CÓ LÃI) */}
                      <div style={{ borderLeft: `1px solid ${colors.border}`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: colors.warn, fontWeight: 'bold', marginBottom: '4px' }}>Tỷ lệ Đang Lãi (Win Rate %):</span>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                          <span style={{ display: 'flex', gap: '12px' }}>
                            <span style={{ width: '60px' }}>🌍 Global:</span>
                            <span style={{ color: colors.green }}>L: {res.globalLongWinRate.toFixed(1)}%</span>
                            <span style={{ color: colors.red }}>S: {res.globalShortWinRate.toFixed(1)}%</span>
                          </span>
                          <span style={{ display: 'flex', gap: '12px' }}>
                            <span style={{ width: '60px' }}>🐋 Whale:</span>
                            <span style={{ color: colors.green }}>L: {res.whaleLongWinRate.toFixed(1)}%</span>
                            <span style={{ color: colors.red }}>S: {res.whaleShortWinRate.toFixed(1)}%</span>
                          </span>
                          <span style={{ display: 'flex', gap: '12px' }}>
                            <span style={{ width: '60px' }}>🐟 Retail:</span>
                            <span style={{ color: colors.green }}>L: {res.retailLongWinRate.toFixed(1)}%</span>
                            <span style={{ color: colors.red }}>S: {res.retailShortWinRate.toFixed(1)}%</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-start', flexWrap: 'wrap' }}>
                    <button onClick={() => window.open(`https://www.binance.com/vi/futures/${res.symbol}`, '_blank')} style={{ ...baseStyles.select, border: `1px solid ${colors.border}`, fontSize: '12px', padding: '6px 12px' }}>📈 CHART</button>
                    <button onClick={() => onOpenRadar && onOpenRadar(res.symbol)} style={{ ...baseStyles.select, border: `1px solid ${colors.green}`, color: colors.green, background: colors.greenBg, fontSize: '12px', padding: '6px 12px', fontWeight: 'bold' }}>📊 DÒ CẢN S/R</button>
                    {/* [BỔ SUNG MỚI]: Nút ĐỌC MA */}
                    <button 
                      onClick={() => checkMA(res.symbol, res.livePrice)} 
                      disabled={loadingMA[res.symbol]} 
                      style={{ ...baseStyles.select, border: `1px solid ${colors.warn}`, color: colors.warn, background: 'rgba(234, 179, 8, 0.1)', fontSize: '12px', padding: '6px 12px', fontWeight: 'bold' }}
                    >
                      {loadingMA[res.symbol] ? '⏳ ĐANG ĐỌC...' : '📉 ĐỌC MA (1H)'}
                    </button>
                  </div>
                </div>

                {/* [BỔ SUNG MỚI]: Bảng Hiển thị Kết quả MA */}
                {maReports[res.symbol] && (
                  <div style={{ marginTop: '4px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '6px', borderLeft: `3px solid ${colors.warn}` }}>
                    <div style={{ color: colors.textMain, fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ fontWeight: 'bold', color: colors.warn, marginBottom: '4px' }}>📊 PHÂN TÍCH MA (KHUNG 1H):</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                        <span>
                          • MA7 so với MA25: 
                          <strong style={{ color: maReports[res.symbol].isMa7Above ? colors.green : colors.red, marginLeft: '4px' }}>
                            {maReports[res.symbol].isMa7Above ? 'NẰM TRÊN' : 'NẰM DƯỚI'} 
                            ({maReports[res.symbol].diffMa.toFixed(2)}%)
                          </strong>
                        </span>
                        <span>
                          • Giá Live so với MA7: 
                          <strong style={{ color: maReports[res.symbol].isLiveAbove ? colors.green : colors.red, marginLeft: '4px' }}>
                            {maReports[res.symbol].isLiveAbove ? 'NẰM TRÊN' : 'NẰM DƯỚI'} 
                            ({maReports[res.symbol].diffLive.toFixed(2)}%)
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
